import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Chart, registerables } from 'chart.js';
import { ResponsiveTable, ResponsiveTableColumn } from '@/components/common/ResponsiveTable';
import { Badge } from '@/components/common/Badge';
import { analyticsService, KPIs, TimeSeriesData, SessionData } from '@/services/analytics.service';
import { formatPrice } from '@/utils/formatters';
import {
  UserGroupIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
  ShoppingCartIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import '@/styles/dashboard.css';

Chart.register(...registerables);

type Period = 'today' | '7days' | '30days' | 'custom';

export const CustomerBehavior = () => {
  const { t } = useTranslation();
  const [kpis, setKPIs] = useState<KPIs | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesData[]>([]);
  const [sessions, setSessions] = useState<SessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState<Period>('30days');
  const [customDateFrom, setCustomDateFrom] = useState<string>('');
  const [customDateTo, setCustomDateTo] = useState<string>('');
  const [countryFilter, setCountryFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [filterCartStatus, setFilterCartStatus] = useState<string>('all');
  const [filterPurchaseStatus, setFilterPurchaseStatus] = useState<string>('all');
  const [filterMinValue, setFilterMinValue] = useState<string>('');
  const [filterMaxValue, setFilterMaxValue] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Références pour les graphiques Chart.js
  const visitorsChartRef = useRef<HTMLCanvasElement>(null);
  const purchasesCartsChartRef = useRef<HTMLCanvasElement>(null);
  const countriesChartRef = useRef<HTMLCanvasElement>(null);
  const eventsChartRef = useRef<HTMLCanvasElement>(null);
  const visitorsChartInstance = useRef<Chart | null>(null);
  const purchasesCartsChartInstance = useRef<Chart | null>(null);
  const countriesChartInstance = useRef<Chart | null>(null);
  const eventsChartInstance = useRef<Chart | null>(null);

  useEffect(() => {
    loadData();
  }, [period, customDateFrom, customDateTo]);

  const getDateRange = (): { dateFrom: string; dateTo: string } => {
    const now = new Date();
    const dateTo = now.toISOString().split('T')[0];

    let dateFrom: string;
    switch (period) {
      case 'today':
        dateFrom = dateTo;
        break;
      case '7days':
        const sevenDaysAgo = new Date(now);
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        dateFrom = sevenDaysAgo.toISOString().split('T')[0];
        break;
      case '30days':
        const thirtyDaysAgo = new Date(now);
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        dateFrom = thirtyDaysAgo.toISOString().split('T')[0];
        break;
      case 'custom':
        dateFrom = customDateFrom || dateTo;
        break;
      default:
        dateFrom = dateTo;
    }

    return { dateFrom, dateTo };
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const { dateFrom, dateTo } = getDateRange();

      const [kpisData, timeSeriesData, sessionsData] = await Promise.all([
        analyticsService.getKPIs(dateFrom, dateTo),
        analyticsService.getTimeSeries(dateFrom, dateTo),
        analyticsService.getSessions(dateFrom, dateTo),
      ]);

      setKPIs(kpisData);
      setTimeSeries(timeSeriesData);
      setSessions(sessionsData);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSessions = sessions.filter(session => {
    // Filtre par pays
    if (countryFilter !== 'all' && session.country !== countryFilter) {
      return false;
    }

    // Filtre par recherche (session_id, user_id, country, city)
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const matchesSearch = 
        session.session_id.toLowerCase().includes(search) ||
        (session.user_id && String(session.user_id).includes(search)) ||
        (session.country && session.country.toLowerCase().includes(search)) ||
        (session.city && session.city.toLowerCase().includes(search));
      if (!matchesSearch) return false;
    }

    // Filtre par statut panier
    if (filterCartStatus !== 'all') {
      if (filterCartStatus === 'yes' && !session.has_cart) return false;
      if (filterCartStatus === 'no' && session.has_cart) return false;
    }

    // Filtre par statut achat
    if (filterPurchaseStatus !== 'all') {
      if (filterPurchaseStatus === 'yes' && !session.has_purchase) return false;
      if (filterPurchaseStatus === 'no' && session.has_purchase) return false;
    }

    // Filtre par valeur min/max
    if (filterMinValue && (session.cart_value || 0) < parseFloat(filterMinValue)) {
      return false;
    }
    if (filterMaxValue && (session.cart_value || 0) > parseFloat(filterMaxValue)) {
      return false;
    }

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredSessions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSessions = filteredSessions.slice(startIndex, endIndex);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, countryFilter, filterCartStatus, filterPurchaseStatus, filterMinValue, filterMaxValue]);

  const uniqueCountries = Array.from(new Set(sessions.map(s => s.country).filter(Boolean)));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const columns: ResponsiveTableColumn<SessionData>[] = [
    {
      key: 'session_id',
      label: t('adminBehavior.sessionUser'),
      render: (session) => (
        <div>
          <div className="table-cell-name">
            {session.user_id ? `User #${session.user_id}` : session.session_id.substring(0, 20) + '...'}
          </div>
          <div className="text-xs text-body">{t('adminBehavior.eventsCount', { count: session.event_count })}</div>
        </div>
      ),
    },
    {
      key: 'location',
      label: t('adminBehavior.location'),
      render: (session) => (
        <div>
          <div className="table-cell-name">{session.country || t('adminBehavior.unknown')}</div>
          <div className="text-xs text-body">{session.city || '-'}</div>
        </div>
      ),
    },
    {
      key: 'first_visit',
      label: t('adminBehavior.firstVisit'),
      render: (session) => <strong>{formatDate(session.first_visit)}</strong>,
    },
    {
      key: 'pages',
      label: t('adminBehavior.pagesVisited'),
      render: (session) => (
        <div className="product-cell">
          {session.pages.length > 0 ? (
            <span>
              <strong>{session.pages[0]?.title || session.pages[0]?.url || '-'}</strong>
              {session.pages.length > 1 && (
                <span className="text-body"> {t('adminBehavior.otherPages', { count: session.pages.length - 1 })}</span>
              )}
            </span>
          ) : (
            '-'
          )}
        </div>
      ),
      mobileHidden: true,
    },
    {
      key: 'has_cart',
      label: t('adminBehavior.cart'),
      render: (session) => (
        <Badge variant={session.has_cart ? 'success' : 'neutral'}>
          {session.has_cart ? t('adminBehavior.yes') : t('adminBehavior.no')}
        </Badge>
      ),
    },
    {
      key: 'has_purchase',
      label: t('adminBehavior.purchase'),
      render: (session) => (
        <Badge variant={session.has_purchase ? 'success' : 'neutral'}>
          {session.has_purchase ? t('adminBehavior.yes') : t('adminBehavior.no')}
        </Badge>
      ),
    },
    {
      key: 'cart_value',
      label: t('adminBehavior.value'),
      render: (session) => (
        <div className="table-cell-number">
          {session.cart_value ? <strong>{formatPrice(session.cart_value)}</strong> : '-'}
        </div>
      ),
    },
  ];

  // Calculer les données pour les graphiques
  const getCountriesData = () => {
    const countryCounts: Record<string, number> = {};
    sessions.forEach(session => {
      const country = session.country || t('adminBehavior.unknown');
      countryCounts[country] = (countryCounts[country] || 0) + 1;
    });
    return Object.entries(countryCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5); // Top 5 pays
  };

  const getEventsData = () => {
    const eventCounts: Record<string, number> = {
      [t('adminBehavior.purchases')]: sessions.filter(s => s.has_purchase).length,
      [t('adminBehavior.cartsCreated')]: sessions.filter(s => s.has_cart).length,
      [t('adminBehavior.sessions')]: sessions.length,
      [t('adminBehavior.abandonedCartsCount')]: sessions.filter(s => s.has_cart && !s.has_purchase).length,
    };
    return Object.entries(eventCounts);
  };

  // Créer/mettre à jour les graphiques
  useEffect(() => {
    if (loading || !timeSeries.length) return;

    // Graphique 1: Évolution des visiteurs (ligne)
    if (visitorsChartRef.current) {
      const ctx = visitorsChartRef.current.getContext('2d');
      if (ctx) {
        if (visitorsChartInstance.current) {
          visitorsChartInstance.current.destroy();
        }
        visitorsChartInstance.current = new Chart(ctx, {
          type: 'line',
          data: {
            labels: timeSeries.map(d => {
              const date = new Date(d.date);
              return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
            }),
            datasets: [{
              label: t('adminBehavior.visitors'),
              data: timeSeries.map(d => d.visitors),
              borderColor: '#8C1843',
              backgroundColor: 'rgba(140, 24, 67, 0.1)',
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              y: { beginAtZero: true }
            }
          }
        });
      }
    }

    // Graphique 2: Achats vs Paniers créés (barres)
    if (purchasesCartsChartRef.current) {
      const ctx = purchasesCartsChartRef.current.getContext('2d');
      if (ctx) {
        if (purchasesCartsChartInstance.current) {
          purchasesCartsChartInstance.current.destroy();
        }
        purchasesCartsChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: timeSeries.map(d => {
              const date = new Date(d.date);
              return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
            }),
            datasets: [
              {
                label: t('adminBehavior.purchases'),
                data: timeSeries.map(d => d.purchases),
                backgroundColor: '#10B981',
              },
              {
                label: t('adminBehavior.cartsCreated'),
                data: timeSeries.map(d => d.carts_created),
                backgroundColor: '#F59E0B',
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: 'top' }
            },
            scales: {
              y: { beginAtZero: true }
            }
          }
        });
      }
    }

    // Graphique 3: Répartition par pays (donut)
    if (countriesChartRef.current) {
      const ctx = countriesChartRef.current.getContext('2d');
      if (ctx) {
        if (countriesChartInstance.current) {
          countriesChartInstance.current.destroy();
        }
        const countriesData = getCountriesData();
        const colors = ['#8C1843', '#10B981', '#3B82F6', '#F59E0B', '#EF4444'];
        countriesChartInstance.current = new Chart(ctx, {
          type: 'doughnut',
          data: {
            labels: countriesData.map(([country]) => country),
            datasets: [{
              data: countriesData.map(([, count]) => count),
              backgroundColor: colors.slice(0, countriesData.length),
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: true, position: 'right' }
            }
          }
        });
      }
    }

    // Graphique 4: Événements (barres horizontales)
    if (eventsChartRef.current) {
      const ctx = eventsChartRef.current.getContext('2d');
      if (ctx) {
        if (eventsChartInstance.current) {
          eventsChartInstance.current.destroy();
        }
        const eventsData = getEventsData();
        eventsChartInstance.current = new Chart(ctx, {
          type: 'bar',
          data: {
            labels: eventsData.map(([event]) => event),
            datasets: [{
              label: 'Nombre',
              data: eventsData.map(([, count]) => count),
              backgroundColor: '#8C1843',
            }]
          },
          options: {
            indexAxis: 'y',
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
              legend: { display: false }
            },
            scales: {
              x: { beginAtZero: true }
            }
          }
        });
      }
    }

    return () => {
      if (visitorsChartInstance.current) visitorsChartInstance.current.destroy();
      if (purchasesCartsChartInstance.current) purchasesCartsChartInstance.current.destroy();
      if (countriesChartInstance.current) countriesChartInstance.current.destroy();
      if (eventsChartInstance.current) eventsChartInstance.current.destroy();
    };
  }, [timeSeries, sessions, loading, t]);

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center' }}>
                <div style={{ color: '#999' }}>{t('adminBehavior.loadingAnalytics')}</div>
              </div>
    );
  }

  return (
    <>

    {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h1 style={{ fontSize: '28px', fontWeight: 700, margin: 0 }}>{t('adminBehavior.title')}</h1>
              
              {/* Filtres de période */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                <select
                  className="filter-select"
                  value={period}
                  onChange={(e) => setPeriod(e.target.value as Period)}
                  style={{ padding: '10px 16px' }}
                >
                  <option value="today">{t('adminBehavior.today')}</option>
                  <option value="7days">{t('adminBehavior.last7Days')}</option>
                  <option value="30days">{t('adminBehavior.last30Days')}</option>
                  <option value="custom">{t('adminBehavior.custom')}</option>
                </select>
                
                {period === 'custom' && (
                  <>
                    <input
                      type="date"
                      value={customDateFrom}
                      onChange={(e) => setCustomDateFrom(e.target.value)}
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                    <span>{t('adminBehavior.to')}</span>
                    <input
                      type="date"
                      value={customDateTo}
                      onChange={(e) => setCustomDateTo(e.target.value)}
                      style={{ padding: '10px', border: '1px solid #ddd', borderRadius: '6px' }}
                    />
                  </>
                )}
              </div>
            </div>

            {/* KPI Cards */}
            <div className="kpi-row">
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <UserGroupIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#3B82F6' }} />
                    <h3 className="kpi-title">{t('adminBehavior.visitors')}</h3>
                  </div>
                  <span className="kpi-index">#1</span>
                </div>
                <div className="kpi-value">{loading ? '...' : (kpis?.visitors || 0).toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminBehavior.uniqueSessions')}</div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <CurrencyDollarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#10B981' }} />
                    <h3 className="kpi-title">{t('adminBehavior.paidCustomers')}</h3>
                  </div>
                  <span className="kpi-index">#2</span>
                </div>
                <div className="kpi-value">{loading ? '...' : (kpis?.paidCustomers || 0).toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{t('adminBehavior.ordersCount', { count: kpis?.paidOrders || 0 })}</div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ChartBarIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#8C1843' }} />
                    <h3 className="kpi-title">{t('adminBehavior.conversionRate')}</h3>
                  </div>
                  <span className="kpi-index">#3</span>
                </div>
                <div className="kpi-value">{loading ? '...' : (kpis?.conversionRate || 0).toFixed(2)}%</div>
                <div className="kpi-period">
                  {(kpis?.visitors || 0) > 0 
                    ? `${kpis?.paidCustomers || 0} / ${kpis?.visitors || 0}`
                    : 'N/A'}
                </div>
              </div>
              
              <div className="kpi-card">
                <div className="kpi-card-header">
                  <div className="kpi-header-content">
                    <ShoppingCartIcon className="kpi-icon" style={{ width: '24px', height: '24px', color: '#F59E0B' }} />
                    <h3 className="kpi-title">{t('adminBehavior.abandonedCarts')}</h3>
                  </div>
                  <span className="kpi-index">#4</span>
                </div>
                <div className="kpi-value">{loading ? '...' : (kpis?.abandonedCarts || 0).toLocaleString('fr-FR')}</div>
                <div className="kpi-period">{formatPrice(kpis?.abandonedValue || 0)}</div>
              </div>
            </div>

            {/* Graphiques détaillés */}
            <div className="charts-row-responsive" style={{ marginTop: '24px' }}>
              {/* Graphique 1: Évolution des visiteurs (ligne) */}
              <div className="table-card">
                <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>{t('adminBehavior.visitorsEvolution')}</h3>
                <div style={{ height: '300px', padding: '20px' }}>
                  {timeSeries.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminBehavior.noData')}
                    </div>
                  ) : (
                    <canvas ref={visitorsChartRef}></canvas>
                  )}
                </div>
              </div>

              {/* Graphique 2: Achats vs Paniers créés (barres) */}
              <div className="table-card">
                <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>{t('adminBehavior.purchasesVsCarts')}</h3>
                <div style={{ height: '300px', padding: '20px' }}>
                  {timeSeries.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminBehavior.noData')}
                    </div>
                  ) : (
                    <canvas ref={purchasesCartsChartRef}></canvas>
                  )}
                </div>
              </div>

              {/* Graphique 3: Répartition par pays (donut) */}
              <div className="table-card">
                <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>{t('adminBehavior.byCountry')}</h3>
                <div style={{ height: '300px', padding: '20px' }}>
                  {sessions.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminBehavior.noData')}
                    </div>
                  ) : (
                    <canvas ref={countriesChartRef}></canvas>
                  )}
                </div>
              </div>

              {/* Graphique 4: Événements (barres horizontales) */}
              <div className="table-card">
                <h3 style={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px' }}>{t('adminBehavior.eventsSummary')}</h3>
                <div style={{ height: '300px', padding: '20px' }}>
                  {sessions.length === 0 ? (
                    <div style={{ width: '100%', textAlign: 'center', color: '#999', padding: '40px' }}>
                      {t('adminBehavior.noData')}
                    </div>
                  ) : (
                    <canvas ref={eventsChartRef}></canvas>
                  )}
                </div>
              </div>
            </div>

            {/* Tableau des sessions */}
            <div className="table-card" style={{ marginTop: '24px' }}>
              <div className="table-header">
                <h3 className="table-title">{t('adminBehavior.detailedSessions', { count: filteredSessions.length })}</h3>
                <div className="flex gap-2 items-center">
                  {(searchTerm || countryFilter !== 'all' || filterCartStatus !== 'all' || filterPurchaseStatus !== 'all' || filterMinValue || filterMaxValue) && (
                    <button
                      onClick={() => {
                        setSearchTerm('');
                        setCountryFilter('all');
                        setFilterCartStatus('all');
                        setFilterPurchaseStatus('all');
                        setFilterMinValue('');
                        setFilterMaxValue('');
                      }}
                      className="btn btn-secondary btn-sm"
                    >
                      {t('adminBehavior.resetFilters')}
                    </button>
                  )}
                </div>
              </div>

              {/* Filtres */}
              <div className="filters-bar">
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.search')}</label>
                  <input
                    type="text"
                    placeholder={t('adminBehavior.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.country')}</label>
                  <select
                    className="form-select"
                    value={countryFilter}
                    onChange={(e) => setCountryFilter(e.target.value)}
                  >
                    <option value="all">{t('adminBehavior.allCountries')}</option>
                    {uniqueCountries.map(country => (
                      <option key={country || 'unknown'} value={country || ''}>{country || t('adminBehavior.unknown')}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.cart')}</label>
                  <select
                    className="form-select"
                    value={filterCartStatus}
                    onChange={(e) => setFilterCartStatus(e.target.value)}
                  >
                    <option value="all">{t('adminBehavior.all')}</option>
                    <option value="yes">{t('adminBehavior.withCart')}</option>
                    <option value="no">{t('adminBehavior.withoutCart')}</option>
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.purchase')}</label>
                  <select
                    className="form-select"
                    value={filterPurchaseStatus}
                    onChange={(e) => setFilterPurchaseStatus(e.target.value)}
                  >
                    <option value="all">{t('adminBehavior.all')}</option>
                    <option value="yes">{t('adminBehavior.withPurchase')}</option>
                    <option value="no">{t('adminBehavior.withoutPurchase')}</option>
                  </select>
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.minValue')}</label>
                  <input
                    type="number"
                    placeholder={t('adminBehavior.zero')}
                    value={filterMinValue}
                    onChange={(e) => setFilterMinValue(e.target.value)}
                    className="form-input"
                  />
                </div>
                <div className="form-group-flex">
                  <label className="form-label">{t('adminBehavior.maxValue')}</label>
                  <input
                    type="number"
                    placeholder={t('adminBehavior.infinity')}
                    value={filterMaxValue}
                    onChange={(e) => setFilterMaxValue(e.target.value)}
                    className="form-input"
                  />
                </div>
                {(filterMinValue || filterMaxValue) && (
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => {
                      setFilterMinValue('');
                      setFilterMaxValue('');
                    }}
                  >
                    {t('adminBehavior.reset')}
                  </button>
                )}
              </div>

              <ResponsiveTable
                columns={columns}
                data={paginatedSessions}
                loading={loading}
                loadingMessage={t('adminBehavior.loading')}
                emptyMessage={
                  searchTerm || countryFilter !== 'all' || filterCartStatus !== 'all' || filterPurchaseStatus !== 'all' || filterMinValue || filterMaxValue
                    ? t('adminBehavior.noFilters')
                    : t('adminBehavior.empty')
                }
                mobileCardTitle={(session) => (
                  <div>
                    <div className="table-cell-name mb-1">
                      {session.user_id ? t('adminBehavior.userHash', { id: session.user_id }) : session.session_id.substring(0, 20) + '...'}
                    </div>
                    <div className="text-xs text-body">{session.country || t('adminBehavior.unknown')}</div>
                  </div>
                )}
                mobileCardActions={(session) => (
                  <div className="product-actions" style={{ display: 'flex', gap: '8px' }}>
                    <Badge variant={session.has_cart ? 'success' : 'neutral'}>
                      {t('adminBehavior.cartLabel')} {session.has_cart ? t('adminBehavior.yes') : t('adminBehavior.no')}
                    </Badge>
                    <Badge variant={session.has_purchase ? 'success' : 'neutral'}>
                      {t('adminBehavior.purchaseLabel')} {session.has_purchase ? t('adminBehavior.yes') : t('adminBehavior.no')}
                    </Badge>
                  </div>
                )}
                tableClassName="products-table"
              />

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination-container" style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginTop: '24px',
                  padding: '16px',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div className="pagination-info" style={{ fontSize: '14px', color: '#666' }}>
                    {t('adminBehavior.displaying', { start: startIndex + 1, end: Math.min(endIndex, filteredSessions.length), total: filteredSessions.length })}
                  </div>
                  <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      {t('adminBehavior.previous')}
                    </button>
                    <div className="pagination-pages" style={{ display: 'flex', gap: '4px' }}>
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                        if (
                          page === 1 ||
                          page === totalPages ||
                          (page >= currentPage - 1 && page <= currentPage + 1)
                        ) {
                          return (
    <>

                            <button
                              key={page}
                              className={`btn ${currentPage === page ? 'btn-primary' : 'btn-secondary'} btn-sm`}
                              onClick={() => setCurrentPage(page)}
                              style={{ minWidth: '36px' }}
                            >
                              {page}
                            </button>
                          
    </>
  );
                        } else if (page === currentPage - 2 || page === currentPage + 2) {
                          return <span key={page} style={{ padding: '0 4px' }}>...</span>;
                        }
                        return null;
                      })}
                    </div>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      {t('adminBehavior.next')}
                    </button>
                  </div>
                </div>
              )}
            </div>
  
    </>
  );
};
